import { useEffect, useState } from 'react';
import Image from 'next/legacy/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { gql } from '@apollo/client';
import { withRouter } from 'next/router';
import { generateDesktopNavLinks, insertGameWeek } from './utils';
import { JoinTheHubButton, MobileLoginButton } from './UIElements/Button';
import UltraBadge from './UIElements/UltraBadge';
import { client, getPostsBySearchTerm } from '../../apollo-client';
import NavagationAccordion from '../navigation-accordion';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { NAV_DATA, PLAN_NAMES } from '@lib/constants';
import type { NavBarType } from '@lib/constants';
import {
  isCurrentPath,
  isInActiveHierachy,
  isUltraMember,
  isUserAuthenticated
} from '@lib/utils';
import AnimateFootball from '@components/animate-football';
import ArticleResultsList from '@components/article/results-list';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';
import SearchBox from '@components/SearchBox';
import SearchInput from '@components/SearchInput';
import SubMenu from '@components/sub-menu';
import { useNotificationPreferencesFeatureFlag } from '@hooks/notifications';

const FFHModal = dynamic(() => import('@components/modal'));

const DesktopLoginButton = dynamic<{
  returnTo?: string;
  children: React.ReactNode;
}>(() => import('./UIElements').then((module) => module.DesktopLoginButton));

const SignOutButton = dynamic<{ onClick: () => void }>(() =>
  import('./UIElements').then((module) => module.SignOutButton)
);

const MobileMenuButton = dynamic<{ showMobileMenu: any; toggle: any }>(() =>
  import('./UIElements').then((module) => module.MobileMenuButton)
);

const Header = ({ router }) => {
  const { user, isLoading } = useAuth0User();
  const [searchTerm, setSearchTerm] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchisLoading, setSearchisLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [joinButtonText, setJoinButtonText] = useState<string>('');
  const [subMenuActiveLevel, setSubMenuActiveLevel] = useState(undefined);

  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek?.number ?? null;

  useEffect(() => {
    if (!searchTerm) {
      setSearchResults(null);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!isLoading && isUserAuthenticated(user)) {
      setJoinButtonText('Upgrade');
    } else {
      setJoinButtonText('Join The Hub');
    }
  }, [isLoading, user]);

  const closeSidebar = () => {
    setShowMobileMenu(false);
  };

  const toggle = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const handleSearchInputChange = async (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = async () => {
    setSearchisLoading(true);
    const { data } = await client.query({
      query: gql`
        ${getPostsBySearchTerm(searchTerm)}
      `
    });
    setSearchResults(data);
    setSearchisLoading(false);
  };

  const registerKeyPress = async (e) => {
    if (e.keyCode === 13) {
      handleSearch();
    }
  };

  const hasPath = (path) => path != '/';

  const hasPemissionToViewSubmenuItem = (item) =>
    !item.isHiddenForGuests ||
    (item.isHiddenForGuests && isUserAuthenticated(user));

  const path = router.asPath.split('?')[0];
  // Where the login links send the user back to once they have signed in
  // (FFH-2135). The home page is the callback's own default, so it needs no
  // returnTo of its own.
  const returnTo = hasPath(router.asPath) ? router.asPath : undefined;

  const isNotificationsEnabled = useNotificationPreferencesFeatureFlag();

  const navData = [...NAV_DATA]
    .map((data) => {
      // Hide Ultra 'how it works' page link for Ultra members
      if (data.name === PLAN_NAMES.ULTRA && isUltraMember(user)) {
        return false;
      }
      // Show Ultra Members content page link for Ultra members
      if (data.name === "Ultra Members' Area" && isUltraMember(user)) {
        return data;
      } else if (data.name === "Ultra Members' Area") {
        return false;
      }
      // Hide AI Team Rating for members
      if (data.name === 'AI Team Rating' && isUserAuthenticated(user)) {
        return false;
      }
      if (data.name === 'Notifications' && (!isNotificationsEnabled || !user)) {
        return false;
      }
      if (data?.hasGameweek) {
        return {
          ...data,
          name: insertGameWeek(data?.name, nextGameweek)
        };
      }

      return {
        ...data
      };
    })
    .filter(Boolean);

  const navLinks = navData.filter((item): item is NavBarType => Boolean(item));

  // Divider entries stay in the list - the sub-menu uses them as group
  // headings in its all-tools panel and sheet
  const subnavigationItems = navLinks
    .filter(
      (a) => isInActiveHierachy(path, a) && !a.isTopLevel && !a.hideSubNav
    )
    .filter((item) => hasPemissionToViewSubmenuItem(item));

  const activeTopLevelNavItem = navLinks.find(
    (item) => item.isTopLevel && isInActiveHierachy(path, item)
  );

  return (
    <div className="border-b border-accent-3">
      <FFHModal
        open={searchModalIsOpen}
        handleClose={() => {
          setSearchTerm('');
          setSearchResults(null);
          setSearchModalIsOpen(false);
        }}
      >
        <SearchBox>
          <div className="flex items-center justify-between p-4">
            <div
              className={`flex flex-col md:flex-rows justify-center grow pt-2`}
            >
              <div className="flex flex-col sm:flex-row gap-4 items-center my-3">
                <SearchInput
                  value={searchTerm}
                  handleChange={handleSearchInputChange}
                  label="Enter search term"
                  id="search-modal"
                  onClose={() => setSearchTerm('')}
                  showCloseIcon={searchTerm?.length > 0}
                  onKeyDown={(e) => registerKeyPress(e)}
                  autoFocus
                  showSearchIcon={true}
                />
                <Button variant="primary" onClick={() => handleSearch()}>
                  Search
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-center px-4 overflow-y-scroll">
            {searchisLoading && (
              <div className="h-48 flex items-center justify-center">
                <AnimateFootball height={75} />
              </div>
            )}
            {!searchisLoading && !searchResults && null}
            {!searchisLoading && searchResults && (
              <div className="px-4 search-box-results">
                <ArticleResultsList
                  onArticleClick={() => {
                    setShowMobileMenu(false);
                    setSearchModalIsOpen(false);
                  }}
                  initialData={searchResults}
                  query={getPostsBySearchTerm(searchTerm)}
                  articleClassName="flex-col first:w-full [&_div]:w-full [&_div:last-child]:mt-1"
                />
              </div>
            )}
          </div>
        </SearchBox>
      </FFHModal>
      <div
        className={`lg:hidden nav nav-accordion  ${
          showMobileMenu ? 'nav--open' : ''
        }`}
      >
        <div className="nav__links flex flex-col">
          <div>
            <div className="flex justify-between items-center pt-5 px-4 pb-6">
              <Link
                href="/"
                className="flex lg:mr-2 xl:mr-24 h-[50px] w-[50px]"
              >
                <Image
                  src="/images/hub-logo.svg"
                  alt="Fantasy Football Hub"
                  height={50}
                  width={50}
                />
              </Link>
              <Icon
                name="Close"
                className="text-white h-6 w-6"
                onClick={() => closeSidebar()}
              ></Icon>
            </div>
            <NavagationAccordion
              closeSidebar={() => closeSidebar()}
              data={navData}
              setSearchModalIsOpen={setSearchModalIsOpen}
            />
          </div>
          <div className="px-4 pb-8">
            {!isUserAuthenticated(user) ? (
              <div className="flex space-x-3">
                <JoinTheHubButton
                  onClick={() => closeSidebar()}
                  path={path}
                  text={joinButtonText}
                />
                <MobileLoginButton
                  onClick={() => closeSidebar()}
                  returnTo={returnTo}
                >
                  Log in
                </MobileLoginButton>
              </div>
            ) : (
              <div className="flex space-x-3">
                <JoinTheHubButton
                  onClick={() => closeSidebar()}
                  path={path}
                  text={joinButtonText}
                />
                {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- Using <a> to avoid Next.js prefetching which would trigger logout */}
                <a
                  data-cy="mobile-menu-logout"
                  onClick={() => closeSidebar()}
                  href="/auth/logout"
                  className="rounded-lg text-white py-2 px-3 font-bold font-open-sans bg-transparent border-2 border-white"
                >
                  Log out
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      <nav className="bg-neutral-800 fixed w-full z-50 shadow-300">
        <div className="xl:container max-w-screen-3xl px-4 sm:px-8 md:px-4 mx-auto py-3">
          <div className="relative flex items-center justify-between scrolling-touch no-scrollbar whitespace-nowrap">
            <div className="flex items-center">
              <Link
                href="/"
                className="flex lg:mr-2 xl:mr-24 h-[50px] w-[50px]"
              >
                <Image
                  src="/images/hub-logo.svg"
                  alt="Fantasy Football Hub"
                  height={50}
                  width={50}
                />
              </Link>
              <div className="hidden lg:block">
                <div className="flex">
                  {generateDesktopNavLinks({
                    navData,
                    isUserAuthenticated,
                    status: user,
                    isCurrentPath,
                    path,
                    subMenuActiveLevel,
                    setSubMenuActiveLevel
                  })}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center justify-end">
              <div className="flex-1 hidden px-2 md:hidden lg:ml-6 lg:justify-end">
                <div className="w-full md:max-w-md lg:max-w-xs">
                  <div className="relative">
                    <Icon
                      name={'Search'}
                      style={{ fontSize: '24px' }}
                      className="text-white cursor-pointer"
                      onClick={() => setSearchModalIsOpen(true)}
                    />
                  </div>
                </div>
              </div>
              {isLoading && (
                <div className="w-full lg:hidden">
                  <AnimateFootball height={24} />
                </div>
              )}
              <div className="flex flex-1 px-3 items-center lg:justify-end">
                <div className="flex items-center justify-between w-full lg:w-fit">
                  <div className="relative hidden lg:block">
                    <Icon
                      name={'Search'}
                      style={{ fontSize: '24px' }}
                      className="text-white cursor-pointer hover:scale-105"
                      onClick={() => setSearchModalIsOpen(true)}
                    />
                  </div>
                  <div className="block lg:hidden">
                    {isUltraMember(user) ? (
                      <UltraBadge />
                    ) : (
                      <JoinTheHubButton
                        onClick={() => closeSidebar()}
                        path={path}
                        text={joinButtonText}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="justify-end divide-x-0 block lg:hidden">
                <MobileMenuButton
                  showMobileMenu={showMobileMenu}
                  toggle={toggle}
                />
              </div>
              <div className="hidden lg:contents lg:ml-4">
                {isLoading ? (
                  <div className="w-full">
                    <AnimateFootball height={24} />
                  </div>
                ) : (
                  <div className="flex items-center account-menu">
                    {!isUserAuthenticated(user) ? (
                      <>
                        <DesktopLoginButton returnTo={returnTo}>
                          Log in
                        </DesktopLoginButton>
                        <JoinTheHubButton
                          onClick={() => closeSidebar()}
                          path={path}
                          text={joinButtonText}
                        />
                      </>
                    ) : (
                      <>
                        <Link
                          href="/account"
                          aria-label="Account"
                          className="inline-flex pointer mr-5"
                        >
                          <Icon
                            className="text-white w-6 h-6"
                            name="Person"
                            title="Account"
                          ></Icon>
                        </Link>
                        {isUltraMember(user) ? (
                          <UltraBadge />
                        ) : (
                          <JoinTheHubButton
                            onClick={() => closeSidebar()}
                            path={path}
                            text="Upgrade"
                          />
                        )}
                        <SignOutButton
                          onClick={() => {
                            window.location.href = '/auth/logout';
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="pt-[74px]">
        {path !== '/' &&
          subnavigationItems.some((item) => Boolean(item.url)) && (
            <SubMenu
              items={subnavigationItems}
              label={activeTopLevelNavItem?.name}
            />
          )}
      </div>
    </div>
  );
};

export default withRouter(Header);
